import apiService from './apiService';
import { sanitizeData } from '../utils/dataSanitizer';

/**
 * Frontend Error Logger Service
 * Sends frontend errors to backend for centralized logging
 */
class FrontendErrorLogger {
    constructor() {
        this.isEnabled = true;
        this.maxRetries = 3;
        this.retryDelay = 1000; // 1 second
        this.queue = [];
        this.isProcessing = false;
        
        // Initialize error boundary
        this.setupGlobalErrorHandlers();
    }

    /**
     * Setup global error handlers for unhandled errors
     */
    setupGlobalErrorHandlers() {
        // Handle unhandled JavaScript errors
        window.addEventListener('error', (event) => {
            this.logError({
                name: event.error?.name || 'JavaScriptError',
                message: event.message || 'Unknown error',
                stack: event.error?.stack,
                page: this.getPageContext(),
                user: this.getUserContext(),
                browser: this.getBrowserContext(),
                context: {
                    filename: event.filename,
                    lineno: event.lineno,
                    colno: event.colno,
                    type: 'unhandled_error'
                }
            });
        });

        // Handle unhandled promise rejections
        window.addEventListener('unhandledrejection', (event) => {
            this.logError({
                name: 'UnhandledPromiseRejection',
                message: event.reason?.message || event.reason || 'Unhandled promise rejection',
                stack: event.reason?.stack,
                page: this.getPageContext(),
                user: this.getUserContext(),
                browser: this.getBrowserContext(),
                context: {
                    type: 'unhandled_promise_rejection',
                    reason: event.reason
                }
            });
        });

        // Handle React error boundaries (if using React error boundary)
        window.addEventListener('react-error', (event) => {
            this.logError({
                name: 'ReactError',
                message: event.detail?.error?.message || 'React component error',
                stack: event.detail?.error?.stack,
                componentStack: event.detail?.errorInfo?.componentStack,
                page: this.getPageContext(),
                user: this.getUserContext(),
                browser: this.getBrowserContext(),
                context: {
                    type: 'react_error',
                    errorInfo: event.detail?.errorInfo
                }
            });
        });
    }

    /**
     * Log an error to the backend
     * @param {Object} errorData - Error information
     */
    async logError(errorData) {
        if (!this.isEnabled) {
            return;
        }

        try {
            const errorLog = this.formatErrorLog(errorData);
            await this.sendErrorLog(errorLog);
        } catch (error) {
            console.error('Failed to log error to backend:', error);
            // Add to queue for retry
            this.queue.push(errorData);
            this.processQueue();
        }
    }

    /**
     * Format error data for backend
     * @param {Object} errorData - Raw error data
     * @returns {Object} Formatted error log
     */
    formatErrorLog(errorData) {
        return {
            // Error details
            message: errorData.message || 'Unknown error',
            stack: errorData.stack,
            componentStack: errorData.componentStack,
            name: errorData.name || 'Error',
            
            // Context objects
            user: errorData.user || this.getUserContext(),
            browser: errorData.browser || this.getBrowserContext(),
            page: errorData.page || this.getPageContext(),
            context: errorData.context || {},
            
            // Metadata
            timestamp: new Date().toISOString(),
            environment: process.env.NODE_ENV || 'development',
            version: process.env.REACT_APP_VERSION || '1.0.0'
        };
    }

    /**
     * Send error log to backend
     * @param {Object} errorLog - Formatted error log
     */
    async sendErrorLog(errorLog) {
        try {
            await apiService.post('/v1/logs/error', errorLog, false); // No auth required
        } catch (error) {
            // If backend is unavailable, store in localStorage for later
            this.storeErrorLocally(errorLog);
            throw error;
        }
    }

    /**
     * Store error in localStorage for later sending
     * @param {Object} errorLog - Error log to store
     */
    storeErrorLocally(errorLog) {
        try {
            const storedErrors = JSON.parse(localStorage.getItem('pendingErrorLogs') || '[]');
            storedErrors.push({
                ...errorLog,
                storedAt: new Date().toISOString()
            });
            
            // Keep only last 50 errors to prevent localStorage bloat
            if (storedErrors.length > 50) {
                storedErrors.splice(0, storedErrors.length - 50);
            }
            
            localStorage.setItem('pendingErrorLogs', JSON.stringify(storedErrors));
        } catch (e) {
            console.error('Failed to store error locally:', e);
        }
    }

    /**
     * Process queued errors with retry logic
     */
    async processQueue() {
        if (this.isProcessing || this.queue.length === 0) {
            return;
        }

        this.isProcessing = true;

        while (this.queue.length > 0) {
            const errorData = this.queue.shift();
            let retries = 0;

            while (retries < this.maxRetries) {
                try {
                    const errorLog = this.formatErrorLog(errorData);
                    await this.sendErrorLog(errorLog);
                    break; // Success, move to next error
                } catch (error) {
                    retries++;
                    if (retries < this.maxRetries) {
                        await this.delay(this.retryDelay * retries); // Exponential backoff
                    }
                }
            }
        }

        this.isProcessing = false;
    }

    /**
     * Send any stored errors from localStorage
     */
    async sendStoredErrors() {
        try {
            const storedErrors = JSON.parse(localStorage.getItem('pendingErrorLogs') || '[]');
            if (storedErrors.length === 0) {
                return;
            }

            for (const errorLog of storedErrors) {
                try {
                    await this.sendErrorLog(errorLog);
                } catch (error) {
                    console.error('Failed to send stored error:', error);
                    break; // Stop if backend is still unavailable
                }
            }

            // Clear stored errors after successful sending
            localStorage.removeItem('pendingErrorLogs');
        } catch (error) {
            console.error('Failed to process stored errors:', error);
        }
    }

    /**
     * Get current page context
     * @returns {Object} Page context
     */
    getPageContext() {
        return {
            url: window.location.href,
            path: window.location.pathname,
            referrer: document.referrer,
            title: document.title
        };
    }

    /**
     * Get current user context
     * @returns {Object} User context
     */
    getUserContext() {
        try {
            const userInfo = JSON.parse(localStorage.getItem('user_info') || '{}');
            return {
                username: userInfo.username || 'anonymous',
                userId: userInfo.id || null,
                isAuthenticated: !!localStorage.getItem('token')
            };
        } catch (error) {
            return {
                username: 'anonymous',
                userId: null,
                isAuthenticated: false
            };
        }
    }

    /**
     * Get browser context
     * @returns {Object} Browser context
     */
    getBrowserContext() {
        return {
            userAgent: navigator.userAgent,
            language: navigator.language,
            platform: navigator.platform,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            viewport: `${window.innerWidth}x${window.innerHeight}`
        };
    }

    /**
     * Utility function for delays
     * @param {number} ms - Milliseconds to delay
     */
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Enable/disable error logging
     * @param {boolean} enabled - Whether to enable logging
     */
    setEnabled(enabled) {
        this.isEnabled = enabled;
    }

    /**
     * Log a custom error with additional context
     * @param {Error} error - Error object
     * @param {Object} context - Additional context
     */
    logCustomError(error, context = {}) {
        this.logError({
            name: error.name,
            message: error.message,
            stack: error.stack,
            context: {
                ...context,
                type: 'custom_error'
            }
        });
    }

    /**
     * Log an API error
     * @param {Object} error - API error object
     * @param {string} endpoint - API endpoint that failed
     * @param {Object} requestData - Request data (sanitized)
     */
    logApiError(error, endpoint, requestData = {}) {
        this.logError({
            name: 'APIError',
            message: error.message || 'API request failed',
            stack: error.stack,
            context: {
                type: 'api_error',
                endpoint,
                status: error.response?.status,
                statusText: error.response?.statusText,
                requestData: this.sanitizeRequestData(requestData)
            }
        });
    }

    /**
     * Sanitize request data to remove sensitive information
     * Uses the comprehensive dataSanitizer utility
     * @param {Object} data - Request data
     * @returns {Object} Sanitized data
     */
    sanitizeRequestData(data) {
        try {
            return sanitizeData(data);
        } catch (error) {
            console.error('Failed to sanitize request data:', error);
            return {
                sanitizationError: 'Failed to sanitize data',
                originalDataType: typeof data
            };
        }
    }
}

// Create singleton instance
const frontendErrorLogger = new FrontendErrorLogger();

// Send stored errors on app startup
setTimeout(() => {
    frontendErrorLogger.sendStoredErrors();
}, 2000); // Wait 2 seconds for app to initialize

export default frontendErrorLogger;
